import { Path as IconPath } from '@ahamove/icons/map';
import { getAddressLabels } from '@ahm/api-wrappers-core';
import { FloatingLabelInput } from '@ahm/react-ui';
import {
  Box,
  Button,
  Stack,
  Text,
  ActionIcon,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { useLocale } from 'next-intl';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { PathRecipientInfo, PathSenderInfo } from './types';
import EditAddressIcon from '@/assets/icons/edit-blue-icon.svg';
import { AddressAutocomplete } from '@/components/address-autocomplete';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

interface FormData {
  sender: PathSenderInfo;
  recipient: PathRecipientInfo;
}

export function DropOffFormSection() {
  const isMobile = useIsMobile();
  const curLocale = useLocale();
  const theme = useMantineTheme();

  const [editingRecipientAddress, setEditingRecipientAddress] =
    useState<boolean>(false);

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<FormData>();

  return (
    <Stack gap={rem(16)}>
      <Box pos="relative" w="100%">
        <Controller
          name="recipient.address"
          control={control}
          render={({ field: { onChange, value } }) => {
            const [firstLabel] = getAddressLabels(value ?? '');
            return (
              <>
                {!editingRecipientAddress && value ? (
                  <Button
                    variant="outline"
                    fullWidth
                    h="auto"
                    leftSection={<IconPath />}
                    rightSection={
                      <ActionIcon variant="transparent">
                        <EditAddressIcon />
                      </ActionIcon>
                    }
                    styles={{
                      root: {
                        padding: rem(12),
                        borderColor: theme.colors.neutral[3],
                      },
                      label: {
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        flex: 1,
                      },
                    }}
                    onClick={() => setEditingRecipientAddress(true)}
                  >
                    <Stack align="flex-start" gap={4}>
                      <Text size="sm" fw={700} c="neutral.7" lineClamp={1}>
                        {firstLabel}
                      </Text>
                      <Text size="xs" c="neutral.5" lineClamp={1} fw={500}>
                        {value}
                      </Text>
                    </Stack>
                  </Button>
                ) : (
                  <Box
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget) && value) {
                        setEditingRecipientAddress(false);
                      }
                    }}
                  >
                    <AddressAutocomplete
                      pathIndex={1}
                      withSavedPlace
                      pointType="dropoff"
                      inputProps={{
                        variant: 'filled',
                        color: 'neutral',
                        w: '100%',
                        autoFocus: true,
                      }}
                      lang={curLocale}
                      isMobile={isMobile}
                      onChange={(addressData) => {
                        if (addressData) {
                          onChange(addressData.address);
                          if (addressData.lat && addressData.lng) {
                            setValue('recipient.lat', addressData.lat);
                            setValue('recipient.lng', addressData.lng);
                          }
                          setEditingRecipientAddress(false);
                        }
                      }}
                      address={{
                        address: value,
                        lat: getValues('recipient.lat'),
                        lng: getValues('recipient.lng'),
                      }}
                      withinPortal={false}
                    />
                  </Box>
                )}
              </>
            );
          }}
        />
      </Box>
      <Controller
        name="recipient.remarks"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FloatingLabelInput
            label="Ghi chú địa chỉ nhận"
            value={value}
            onChange={(e) => onChange(e)}
            h="auto"
          />
        )}
      />
      <Controller
        name="recipient.name"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FloatingLabelInput
            label="Tên người nhận"
            h="auto"
            value={value}
            onChange={onChange}
            error={errors.recipient?.name?.message}
          />
        )}
      />
      <Controller
        name="recipient.mobile"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FloatingLabelInput
            label="Số điện thoại người nhận"
            h="auto"
            value={value}
            onChange={onChange}
            error={errors.recipient?.mobile?.message}
          />
        )}
      />
      <Controller
        name="recipient.tracking_number"
        control={control}
        render={({ field: { value } }) => (
          <FloatingLabelInput
            label="Mã vận đơn"
            h="auto"
            value={value}
            readOnly
            disabled
          />
        )}
      />
      <Controller
        name="recipient.cod"
        control={control}
        render={({ field: { value } }) => (
          <FloatingLabelInput
            label="COD"
            h="auto"
            type="number"
            value={value?.toString() ?? ''}
            readOnly
            disabled
          />
        )}
      />
      <Controller
        name="recipient.item_value"
        control={control}
        render={({ field: { value } }) => (
          <FloatingLabelInput
            label="Giá trị hàng hóa"
            h="auto"
            type="number"
            value={value?.toString() ?? ''}
            readOnly
            disabled
          />
        )}
      />
    </Stack>
  );
}
